import { Info } from "lucide-react";
import {
  LicenseSourceDetails,
  type SourceCheck,
} from "./license-source-details";
import type { ReportItem, RunScope } from "../types";

/** Neutral, not amber: a difference between sources is information. */
const SOURCE_CHIP_STYLE = "border-slate-200 bg-slate-50 text-slate-700";

type License = NonNullable<ReportItem["license"]>;

/** What differs between a license's sources, and each source's state. It
 * explains the verdict; it never raises an issue of its own. */
export function LicenseSourceSummary({ license }: { license: License }) {
  return (
    <div className="space-y-2">
      {license.differences.length > 0 && (
        <section
          aria-label="Sources differ"
          className="space-y-1.5 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs leading-5 text-slate-700"
        >
          <h4 className="flex items-center gap-1.5 font-semibold">
            <Info className="size-4 shrink-0" aria-hidden />
            Sources differ
          </h4>
          <ul className="list-disc space-y-1 pl-5">
            {license.differences.map((difference) => (
              <li key={difference}>{difference}</li>
            ))}
          </ul>
        </section>
      )}
      {/* With one source, its own section below already names it. */}
      {license.sources.length > 1 && (
        <ul aria-label="Sources" className="flex flex-wrap gap-1.5">
          {license.sources.map((source) => (
            <li
              key={`${source.sourceId}-${source.checkId}`}
              className={`rounded-full border px-2 py-0.5 text-xs font-medium ${SOURCE_CHIP_STYLE}`}
            >
              {source.sourceName} · {source.stateLabel}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Each source's name, state and evidence, the deciding source first. A
 * license with one source shows that source's details directly: its name is
 * the licensing authority in the record and its state is the item's verdict. */
export function LicenseSources({
  license,
  checks,
  runId,
  scope,
  issuesShownAbove,
}: {
  license: License;
  checks: SourceCheck[];
  runId: string;
  scope: RunScope;
  /** The item's banner already states the deciding source's issues. */
  issuesShownAbove: boolean;
}) {
  const sources = [...license.sources].sort(
    (a, b) => Number(b.decides) - Number(a.decides),
  );
  const hasSeveralSources = sources.length > 1;
  return (
    <>
      {sources.map((source) => {
        const check = checks.find((entry) => entry.id === source.checkId);
        // A board check held back until FSMB finishes has no check yet.
        const details = check && (
          <LicenseSourceDetails
            source={source}
            check={check}
            runId={runId}
            scope={scope}
            showIssues={!(source.decides && issuesShownAbove)}
          />
        );
        if (!hasSeveralSources) {
          return (
            <div key={`${source.sourceId}-${source.checkId}`}>{details}</div>
          );
        }
        return (
          <section
            key={`${source.sourceId}-${source.checkId}`}
            aria-label={source.sourceName}
            className="space-y-3 rounded-lg border p-4"
          >
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="text-sm font-semibold">{source.sourceName}</h4>
              <span
                className={`rounded-full border px-2 py-0.5 text-xs font-medium ${SOURCE_CHIP_STYLE}`}
              >
                {source.stateLabel}
              </span>
            </div>
            {details}
          </section>
        );
      })}
    </>
  );
}
